import { Link } from 'react-router-dom';
import { Mock, Photo } from '../components/Mock';
import { Section } from '../components/Section';
import { ShareButtons } from '../components/ShareButtons';
import { googleCalendarUrl, icsDataUrl, type CalendarEvent } from '../lib/calendar';
import { clockTime, stayDate } from '../lib/format';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { SEO } from '../seo/pages';

/**
 * Saturday is the welcome night and the celebration (doors at six, dinner nine
 * to eleven, wrap around midnight); Sunday is the farewell. Times are Eric's
 * from 2026-09-08; the line items around them are sample copy.
 */
const SCHEDULE = [
  {
    day: 'Saturday',
    label: 'Welcome night',
    items: [
      ['3:00 PM', 'Check-in opens; give the group name BrandonStrong at the front desk'],
      ['6:00 PM', 'Doors. The celebration starts in the ballroom with the music up'],
      ['9:00 PM', 'Dinner is served, until eleven'],
      ['11:00 PM', 'Dinner wraps and the music comes back up'],
      ['12:00 AM', 'Last call. We wrap around midnight; the lobby bar is on its own after that'],
    ],
  },
  {
    day: 'Sunday',
    label: 'Farewell',
    items: [
      ['8:00 AM', 'Hot breakfast in the lobby, included with every room, until ten'],
      ['10:30 AM', 'Group photo on the front steps; tag it #BrandonStrong'],
      ['12:00 PM', 'Check-out and the farewell in the parking lot; late check-out on request'],
    ],
  },
];

export function EventPage() {
  usePageMeta(SEO.event.title, SEO.event.description, { jsonLd: SEO.event.jsonLd });
  const { data: s } = useSettings();
  const mapsUrl = s?.hotelAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.hotelAddress)}`
    : null;
  const when = s?.eventStartDate
    ? s.eventEndDate && s.eventEndDate !== s.eventStartDate
      ? `${stayDate(s.eventStartDate)} to ${stayDate(s.eventEndDate)}`
      : stayDate(s.eventStartDate)
    : 'Dates coming soon';
  const link = 'text-pink-300 underline hover:text-pink-200';
  const calendarEvent: CalendarEvent | null = s?.eventStartDate
    ? {
        title: s.eventName || 'Celebration of Life for Brandon',
        start: s.eventStartDate,
        end: s.eventEndDate ?? s.eventStartDate,
        location: [s.hotelName, s.hotelAddress].filter(Boolean).join(', ') || null,
        description:
          'Saturday welcome night from 6 PM, dinner 9 to 11 PM. Sunday farewell. Rooms, schedule and directions at brandonstrong.com',
        url: 'https://brandonstrong.com/event',
      }
    : null;

  return (
    <>
      <Section eyebrow="The celebration" title={s?.eventName ?? 'Celebration of Life'}>
        <Mock className="-mt-4 mb-10 max-w-3xl">
          <p className="text-xl text-ink-100">
            A weekend, not a service. Saturday is the welcome night: doors at six, dinner from nine
            to eleven, and the music runs until we wrap around midnight. Sunday is the farewell,
            with breakfast, the group photo, and hugs in the parking lot. The family is holding 62
            rooms for Saturday night, so book yours and stay over.
          </p>
        </Mock>
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
            <div>
              <dt className="text-meta text-sm">When</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {when}
                <span className="block text-sm text-ink-400">
                  Saturday from 6:00 PM, dinner 9:00 to 11:00 PM, wraps around midnight. Sunday
                  farewell.
                </span>
                {calendarEvent && (
                  <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <a
                      href={icsDataUrl(calendarEvent)}
                      download="brandonstrong-celebration.ics"
                      className={link}
                    >
                      Add to calendar
                    </a>
                    <a
                      href={googleCalendarUrl(calendarEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={link}
                    >
                      Google Calendar
                    </a>
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Where</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {s?.hotelName ?? 'Venue coming soon'}
                {s?.hotelAddress && (
                  <span className="block text-sm text-ink-400">{s.hotelAddress}</span>
                )}
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm ${link}`}
                  >
                    Get directions
                  </a>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Dress code</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Rockstar.
                <Mock className="mt-2">
                  <p>
                    Come dressed for the show, not the service. Leather and denim, band tees, boots,
                    studs, sunglasses at night, big hair, glitter if you dare. Black is fine if it
                    is a band shirt; leave the suits and the ties at home. Save the loudest outfit
                    for Saturday at six, and tag your photos #BrandonStrong.
                  </p>
                </Mock>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Rooms</dt>
              <dd className="mt-1 text-lg text-ink-50">
                The family is holding 62 rooms at $299 a night all in, king or two queens.{' '}
                <Link to="/rooms" className={link}>
                  Reserve a room
                </Link>
                {s
                  ? ` (check-in ${clockTime(s.checkInTime)}, check-out ${clockTime(s.checkOutTime)})`
                  : ''}
                . A room is your RSVP.
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">RSVP</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Not staying at the hotel? Please{' '}
                <Link to="/rsvp" className={link}>
                  RSVP
                </Link>{' '}
                with the name of everyone in your party; that is the list at the door. A donation is
                welcome, never required, and all of it goes to Shawna.
              </dd>
            </div>
          </dl>
          <Photo name="event-hero.jpg" alt="The venue" className="aspect-[16/10] w-full" />
        </div>
        <ShareButtons
          className="mt-10"
          title={s?.eventName ?? 'Celebration of Life for Brandon'}
          text={`Join us at the celebration of life for Brandon${when !== 'Dates coming soon' ? `, ${when}` : ''}.`}
        />
      </Section>

      <section className="border-y border-white/10 bg-night-900">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="mb-8 flex items-end justify-between">
            <h2 className="text-4xl">The weekend</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {SCHEDULE.map((d) => (
              <Mock key={d.day}>
                <p className="text-meta text-lg">{d.day}</p>
                <h3 className="mt-1 text-2xl text-pink-500">{d.label}</h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {d.items.map(([time, what]) => (
                    <li key={time} className="flex gap-4">
                      <span className="text-meta w-20 shrink-0 text-sm">{time}</span>
                      <span className="text-ink-200">{what}</span>
                    </li>
                  ))}
                </ul>
              </Mock>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-2">
        <Mock>
          <h2 className="text-3xl">Getting there</h2>
          <ul className="mt-4 flex flex-col gap-3 text-ink-200">
            <li>
              Flying in? Tampa International (TPA) is the closer airport at about 40 miles and 45
              minutes east on I-4. Orlando International (MCO) is about 55 miles and an hour west on
              I-4, and usually has the cheaper fares from the northeast.
            </li>
            <li>
              Driving: the hotel is at 511 West Lime Street in downtown Lakeland, a few minutes off
              I-4. The Get directions link above opens the route in Google Maps.
            </li>
            <li>
              Free self-parking in the hotel lot for every guest, with overflow on the street. Ride
              shares and taxis drop at the front entrance under the canopy.
            </li>
            <li>
              Arriving after doors on Saturday? Call the front desk on the way and they will hold
              your key at reception; come straight to the ballroom.
            </li>
          </ul>
        </Mock>
        <Mock>
          <h2 className="text-3xl">Good to know</h2>
          <ul className="mt-4 flex flex-col gap-3 text-ink-200">
            <li>
              Hot breakfast, Wi-Fi, the outdoor pool, the fitness room and parking are included with
              every room. Every room has a mini fridge and microwave, so stock up on the way in.
            </li>
            <li>
              Rooms are run of the house: the hotel assigns a king or two queens at check-in. Ask
              for a layout or an accessible room in the special requests at checkout.
            </li>
            <li>
              Give the group name at check-in and the front desk will find your reservation; you
              have already paid in full through this site, so only incidentals go on a card at the
              desk.
            </li>
            <li>
              Anything else: see the{' '}
              <Link to="/contact" className={link}>
                FAQ
              </Link>{' '}
              or email the family.
            </li>
          </ul>
        </Mock>
      </section>
    </>
  );
}
